<template>
  <div class="recipe-batch">
    <div class="row">
      <div class="col-lg-5">
        <div class="form-group">
          <label for="recipe-batch-list"><strong>Liste de recettes</strong></label>
          <textarea
            id="recipe-batch-list"
            v-model="list"
            class="form-control"
            rows="16"
            placeholder="Un nom de recette (ou un ID) par ligne, par exemple copié depuis une colonne Excel."></textarea>
          <small class="form-text text-muted">
            Correspondance sur le nom exact (casse et accents ignorés) ou sur l'ID. Les doublons sont ignorés.
            {{ lineCount }} ligne(s).
          </small>
        </div>
        <el-button type="primary" :loading="resolving" :disabled="!list.trim()" @click="resolve">
          Vérifier la liste
        </el-button>
        <el-button :disabled="resolving" @click="reset">Réinitialiser</el-button>
        <div v-if="resolveError" class="alert alert-danger mt-3 mb-0">{{ resolveError }}</div>
      </div>

      <div class="col-lg-7">
        <div v-if="resolved">
          <div class="row mb-3">
            <div class="col-4">
              <div class="alert alert-success mb-0"><strong>{{ recipes.length }}</strong> recette(s) trouvée(s)</div>
            </div>
            <div class="col-4">
              <div :class="['alert', 'mb-0', notFound.length ? 'alert-warning' : 'alert-secondary']">
                <strong>{{ notFound.length }}</strong> ligne(s) sans correspondance
              </div>
            </div>
            <div class="col-4">
              <div :class="['alert', 'mb-0', ambiguous.length ? 'alert-info' : 'alert-secondary']">
                <strong>{{ ambiguous.length }}</strong> nom(s) en double
              </div>
            </div>
          </div>

          <div v-if="notFound.length" class="mb-3">
            <strong>Sans correspondance :</strong>
            <textarea class="form-control mt-1" rows="3" readonly :value="notFound.join('\n')"></textarea>
          </div>

          <div v-if="ambiguous.length" class="alert alert-info">
            Plusieurs recettes portent le même nom, toutes ont été ajoutées :
            <ul class="mb-0">
              <li v-for="a in ambiguous" :key="a.query">{{ a.query }} → IDs {{ a.ids.join(', ') }}</li>
            </ul>
          </div>

          <div class="d-flex flex-wrap align-items-center mb-2" style="gap: 8px;">
            <strong>{{ selectedIds.length }} / {{ recipes.length }} sélectionnée(s)</strong>
            <span class="ml-auto"></span>
            <el-button size="small" type="primary" :disabled="!selectedIds.length" :loading="calculating" @click="calcul">
              Calculer ces recettes
            </el-button>
            <el-button size="small" :disabled="!selectedIds.length" @click="openExport">
              Export EcoBalyse (Excel)
            </el-button>
            <el-button size="small" :disabled="!selectedIds.length" @click="exportJson(5)">
              Export JSON EcoBalyse
            </el-button>
            <el-button size="small" :disabled="!selectedIds.length" @click="exportJson(6)">
              Export JSON EcoBalyse v2
            </el-button>
          </div>

          <el-table
            ref="table"
            :data="recipes"
            size="mini"
            max-height="520"
            border
            @selection-change="onSelectionChange">
            <el-table-column type="selection" width="40"></el-table-column>
            <el-table-column prop="id" label="ID" width="80" sortable></el-table-column>
            <el-table-column prop="name" label="Nom" sortable>
              <template slot-scope="scope">
                <a :href="'/calculateur/' + scope.row.id" target="_blank">{{ scope.row.name }}</a>
              </template>
            </el-table-column>
            <el-table-column prop="user" label="Propriétaire" width="160"></el-table-column>
            <el-table-column prop="calcul_at" label="Dernier calcul" width="130"></el-table-column>
            <el-table-column prop="ecobalyse_date" label="Ecobalyse" width="130"></el-table-column>
          </el-table>

          <form ref="jsonForm" method="POST" :action="exportUrl" style="display: none;">
            <input type="hidden" name="_token" :value="csrf">
            <input type="hidden" name="export" :value="jsonExport">
            <input v-for="id in selectedIds" :key="id" type="hidden" name="recipes[]" :value="id">
          </form>
        </div>
        <div v-else class="text-muted">
          Collez une liste de noms de recettes puis cliquez sur « Vérifier la liste ». Vous pourrez ensuite
          relancer le calcul sur ces seules recettes, puis exporter leurs résultats Ecobalyse.
        </div>
      </div>
    </div>

    <recipe-calcul-progress-modal ref="calculModal" @finished="onCalculFinished"></recipe-calcul-progress-modal>
    <export-columns-modal ref="exportModal" :submit-url="exportUrl" method="post"></export-columns-modal>
  </div>
</template>

<script>
/**
 * Traitement de recettes par liste (noms ou IDs) : résolution côté serveur puis
 * réutilisation du calcul en masse et des exports Ecobalyse existants.
 */
export default {
  props: {
    resolveUrl: { type: String, required: true },
    calculUrl: { type: String, required: true },
    exportUrl: { type: String, required: true },
  },
  data() {
    return {
      list: '',
      jsonExport: 5,
      resolving: false,
      resolved: false,
      resolveError: null,
      recipes: [],
      notFound: [],
      ambiguous: [],
      selectedIds: [],
      calculating: false,
    };
  },
  computed: {
    lineCount() {
      return this.list.split(/\r?\n/).filter((l) => l.trim() !== '').length;
    },
    csrf() {
      const meta = document.querySelector('meta[name="csrf-token"]');
      return meta ? meta.getAttribute('content') : '';
    },
  },
  methods: {
    async resolve() {
      this.resolving = true;
      this.resolveError = null;
      try {
        const { data } = await axios.post(this.resolveUrl, { list: this.list });
        this.recipes = data.recipes || [];
        this.notFound = data.not_found || [];
        this.ambiguous = data.ambiguous || [];
        this.resolved = true;
        this.$nextTick(() => {
          if (this.$refs.table) {
            this.$refs.table.clearSelection();
            this.recipes.forEach((row) => this.$refs.table.toggleRowSelection(row, true));
          }
        });
      } catch (e) {
        this.resolveError = (e.response && e.response.data && e.response.data.error)
          ? e.response.data.error
          : 'Erreur lors de la vérification de la liste.';
      } finally {
        this.resolving = false;
      }
    },
    reset() {
      this.list = '';
      this.resolved = false;
      this.resolveError = null;
      this.recipes = [];
      this.notFound = [];
      this.ambiguous = [];
      this.selectedIds = [];
    },
    onSelectionChange(rows) {
      this.selectedIds = rows.map((r) => r.id);
    },
    async calcul() {
      if (!this.selectedIds.length) return;
      try {
        await this.$confirm(
          'Lancer le calcul (avec appel Ecobalyse) sur ' + this.selectedIds.length + ' recette(s) ?',
          'Confirmation',
          { confirmButtonText: 'Calculer', cancelButtonText: 'Annuler', type: 'warning' }
        );
      } catch (e) {
        return;
      }
      this.calculating = true;
      try {
        const { data } = await axios.post(this.calculUrl, { recipes: this.selectedIds });
        this.$refs.calculModal.open(data.batch_id, data.total);
      } catch (e) {
        this.$message({ type: 'error', message: 'Erreur lors du lancement du calcul.' });
      } finally {
        this.calculating = false;
      }
    },
    onCalculFinished(result) {
      // Recharge les dates de calcul / Ecobalyse des recettes listées avant un éventuel export
      const keep = this.selectedIds.slice();
      this.resolve().then(() => {
        this.$nextTick(() => {
          if (!this.$refs.table) return;
          this.$refs.table.clearSelection();
          this.recipes
            .filter((row) => keep.indexOf(row.id) !== -1)
            .forEach((row) => this.$refs.table.toggleRowSelection(row, true));
        });
      });
      this.$message({
        type: result.error ? 'warning' : 'success',
        message: 'Calcul terminé : ' + result.success + ' réussie(s), ' + result.error + ' en erreur. Vous pouvez exporter les résultats.',
      });
    },
    openExport() {
      if (!this.selectedIds.length) return;
      this.$refs.exportModal.open({ recipes: this.selectedIds });
    },
    exportJson(type) {
      if (!this.selectedIds.length) return;
      this.jsonExport = type;
      this.$nextTick(() => this.$refs.jsonForm.submit());
    },
  },
};
</script>
